import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type AddressInput, formatAddressLine, mapsUrl } from '@shared/maps.ts';

/**
 * An address, shown as a link that opens the device's map app.
 *
 * Wherever an address stands on its own — a visit, a quote, an invoice, a
 * property on the customer page — it is the answer to "where am I going?", and
 * the next thing anybody does with it is put it into a map. This turns that
 * from copy-and-paste into a tap.
 *
 * The link is a plain external link, so everything the browser already does
 * with one still works: long-press to copy, open in a new tab, share to a
 * phone. `src/shared/maps.ts` decides where it points — Apple Maps on Apple
 * devices, Google Maps everywhere else.
 *
 * Where there is no address to map, the children (or the address text) are
 * rendered as they were, unlinked. Callers therefore never have to ask whether
 * an address is mappable before using this.
 */
export interface AddressLinkProps {
  /** The address, either as the Worker's snapshot string or as its fields. */
  address: AddressInput;
  /**
   * What to show. Defaults to the address on one line; pass children where the
   * surrounding layout already prints it its own way — as a block with its
   * newlines kept, or as the first line of a longer one.
   */
  children?: ReactNode;
  /** Replaces the link styling entirely, for the rows that set their own. */
  className?: string;
}

const LINK_CLASS = 'text-[color:var(--color-brand)] hover:underline';

export default function AddressLink({ address, children, className }: AddressLinkProps) {
  const { t } = useTranslation();
  const line = formatAddressLine(address);
  const content = children ?? line;
  const href = mapsUrl(address, userAgent());

  if (!href) return <>{content}</>;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={className ?? LINK_CLASS}
      // The visible text is the address; the label says what following it
      // does, so a screen reader announces a destination rather than reading
      // a street name and leaving the listener to guess it is a link.
      aria-label={t('common.openInMaps', { address: line })}
      title={t('common.openInMaps', { address: line })}
    >
      {content}
    </a>
  );
}

/**
 * The user agent, or null where there is not one — which is what decides Apple
 * Maps against Google Maps. Guarded rather than read directly so that
 * rendering this component somewhere without a DOM produces the Google link
 * instead of throwing.
 */
function userAgent(): string | null {
  return typeof navigator === 'undefined' ? null : navigator.userAgent;
}
